import { computed } from 'vue'
import { minMaxByKey } from '@/utils/objects'
import { getDeliveryTermLabel } from '@/utils/project'
import type { InvestmentProjectSummary, Typology } from '@/server/api/buyer-front/types'
import type { ProjectComposableModule } from '@/composables/useProject/types'

const useProject = (project: InvestmentProjectSummary): ProjectComposableModule.useProjectResult => {
  const { t } = useI18n()
  const { buildImageUrl } = useImsImageProvider()

  const getMinMaxRangeString = (typologies: InvestmentProjectSummary['typologies'], key: keyof Typology): { min: number; max: number; isEqual: boolean; range: string } => {
    const minMax = minMaxByKey(typologies, key)
    if (!minMax) return {}

    return {
      ...minMax,
      range: minMax.isEqual ? `${minMax.min}` : `${minMax.min} - ${minMax.max}`
    }
  }

  const filteredPictures = (type) => {
    const pictures = project.pictures_v2 || []
    if (!type || pictures.length === 0) return []
    return pictures.filter(picture => picture.type === type)
  }

  const deliveryTermLabel = computed<string>(() => {
    return getDeliveryTermLabel(project.delivered_at, t)
  })

  const bedroomRange = computed<Record<string, any>>(() => {
    return getMinMaxRangeString(project.typologies, 'bedroom')
  })

  const bedroomLabel = computed<string>(() => {
    const pluralCount = parkingNumbersRange.value.min ? (parkingNumbersRange.value.min > 1 ? 2 : 1) : 2
    return `${bedroomRange.value.range} ${t('project.bedrooms.long_or_short', pluralCount)}`
  })

  const bathroomRange = computed<Record<string, any>>(() => {
    return getMinMaxRangeString(project.typologies, 'bathroom')
  })

  const bathroomLabel = computed<string>(() => {
    return `${bathroomRange.value.range} ${t('project.bathrooms.exact', bathroomRange.value.max)}`
  })

  const parkingNumbersRange = computed<Record<string, any>>(() => {
    return getMinMaxRangeString(project.typologies, 'parking_numbers')
  })

  const parkingNumbersLabel = computed<string>(() => {
    return `${parkingNumbersRange.value.range} ${t('project.parking_numbers.short')}`
  })

  const priceRange = computed<ProjectComposableModule.MinMaxValues>(() => {
    return getMinMaxRangeString(project.typologies, 'price')
  })

  const amenitiesList = computed<string[]>(() => {
    return [
      bathroomRange.value.range ? bedroomLabel.value : null,
      bedroomRange.value.range ? bathroomLabel.value : null,
      parkingNumbersRange.value.range ? parkingNumbersLabel.value : null
    ].filter(Boolean) as string[]
  })

  const galleryPictures = computed(() => {
    const pictures = filteredPictures('gallery')

    return pictures.map(picture => ({
      src: buildImageUrl(picture.url),
      alt: t('project.coverOf', { name: project.name })
    }))
  })

  return {
    amenitiesList,
    bathroomRange,
    bathroomLabel,
    bedroomRange,
    bedroomLabel,
    deliveryTermLabel,
    galleryPictures,
    parkingNumbersLabel,
    parkingNumbersRange,
    priceRange
  }
}

export default useProject
